import { useEffect, useState, type Dispatch, type FocusEvent, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import './partner-network.css';

const vendors = [
  { name: '1947 Hindustani Cuisine', slug: '1947-hindustani-cuisine', description: 'Onboarded hospitality partner in the BiteWise network.' },
  { name: 'Hibiscus', slug: 'hibiscus', description: 'Restaurant, catering and fulfilment capability.' },
  { name: "Yogi's Cafe", slug: 'yogis-cafe', description: 'Onboarded cafe partner for institutional and event enquiries.' },
  { name: '1900 AD', slug: '1900-ad', description: 'Hospitality partner available for requirement-based sourcing.' },
];

const corporates = [
  { name: 'SAR Group', logo: 'sar-group.png', website: 'https://www.sar-group.com/', compact: true },
  { name: 'Let\u2019s Connect', logo: 'lets-connect.webp', website: 'https://www.letsconnectindia.com/' },
  { name: 'Ofis Square', logo: 'ofis-square.png', website: 'https://ofissquare.com/' },
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return reduced;
}

function useRotation(length: number, enabled: boolean, interval: number) {
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setSelected(index => (index + 1) % length);
    }, interval);
    return () => window.clearInterval(timer);
  }, [enabled, interval, length]);
  return [selected, setSelected] as const;
}

export default function PartnerNetwork({ reduceMotion }: { reduceMotion: boolean }) {
  const deviceReduced = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const [activeCarousel, setActiveCarousel] = useState<'vendor' | 'corporate' | null>(null);
  const rotating = !paused && !activeCarousel && !reduceMotion && !deviceReduced;
  const [vendorSelected, setVendorSelected] = useRotation(vendors.length, rotating, 4200);
  const [corporateSelected, setCorporateSelected] = useRotation(corporates.length, rotating, 5000);
  const vendor = vendors[vendorSelected];
  const corporate = corporates[corporateSelected];
  const pauseLabel = reduceMotion || deviceReduced ? 'Motion reduced' : paused ? 'Resume rotation' : 'Pause rotation';
  const regionEvents = (name: 'vendor' | 'corporate') => ({
    onMouseEnter: () => setActiveCarousel(name),
    onMouseLeave: () => setActiveCarousel(null),
    onFocusCapture: () => setActiveCarousel(name),
    onBlurCapture: (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActiveCarousel(null);
    },
  });

  return <section id="partners" className="bw-partner-network">
    <div className="bw-wrap bw-site-shell bw-partner-network-inner">
      <div className="bw-section-head bw-partner-intro"><div><p className="bw-eyebrow">THE PEOPLE BEHIND THE ECOSYSTEM</p><h2>Great partners.<br /><em>Shared possibilities.</em></h2></div><p>From onboarded hospitality vendors to corporate spaces, our network connects supply with the organisations that need it.</p></div>
      <div className="bw-partner-toolbar"><span>PARTNER NETWORK</span><button type="button" onClick={() => setPaused(value => !value)} disabled={reduceMotion || deviceReduced}>{pauseLabel}</button></div>
      <div className="bw-partner-carousels">
        <div className="bw-vendor-carousel" role="region" aria-roledescription="carousel" aria-label="Onboarded restaurant partners" {...regionEvents('vendor')}>
          <div className="bw-partner-kicker"><span>ONBOARDED RESTAURANT PARTNERS</span><strong>{String(vendorSelected + 1).padStart(2, '0')} / {String(vendors.length).padStart(2, '0')}</strong></div>
          <article className="bw-vendor-slide" role="group" aria-roledescription="slide" aria-label={`${vendorSelected + 1} of ${vendors.length}`} aria-live={rotating ? 'off' : 'polite'}>
            <h3 key={vendor.name}>{vendor.name}</h3><p>{vendor.description}</p><Link to={`/restaurant/${vendor.slug}`}>View partner profile <span aria-hidden="true">&rarr;</span></Link>
          </article>
          <CarouselControls label="restaurant partner" length={vendors.length} selected={vendorSelected} setSelected={setVendorSelected} names={vendors.map(item => item.name)} />
        </div>
        <div className="bw-corporate-carousel" role="region" aria-roledescription="carousel" aria-label="Corporate partners" {...regionEvents('corporate')}>
          <div className="bw-partner-kicker"><span>CORPORATE PARTNERS</span><strong>{String(corporateSelected + 1).padStart(2, '0')} / {String(corporates.length).padStart(2, '0')}</strong></div>
          <div className="bw-corporate-slide" role="group" aria-roledescription="slide" aria-label={`${corporateSelected + 1} of ${corporates.length}`} aria-live={rotating ? 'off' : 'polite'}>
            <span>CONNECTED THROUGH BiteWise</span><h3 key={corporate.name} className="bw-corporate-brand"><a href={corporate.website} target="_blank" rel="noopener noreferrer" title={`${corporate.name} official website`}><img className={corporate.compact ? 'bw-corporate-logo bw-corporate-logo--compact' : 'bw-corporate-logo'} src={`/brand-assets/corporates/${corporate.logo}`} alt={corporate.name} /></a></h3><p>Connecting workplace requirements with our partner ecosystem.</p>
          </div>
          <CarouselControls label="corporate partner" length={corporates.length} selected={corporateSelected} setSelected={setCorporateSelected} names={corporates.map(item => item.name)} />
        </div>
      </div>
    </div>
  </section>;
}

function CarouselControls({ label, length, selected, setSelected, names }: { label: string; length: number; selected: number; setSelected: Dispatch<SetStateAction<number>>; names: string[] }) {
  return <div className="bw-carousel-controls"><button type="button" aria-label={`Previous ${label}`} onClick={() => setSelected(index => (index + length - 1) % length)}>&larr;</button><div className="bw-carousel-dots">{names.map((name, index) => <button type="button" key={name} aria-label={`Show ${name}`} aria-pressed={selected === index} onClick={() => setSelected(index)}><span /></button>)}</div><button type="button" aria-label={`Next ${label}`} onClick={() => setSelected(index => (index + 1) % length)}>&rarr;</button></div>;
}
